// Client-side financial metadata — admin-only views.

export const INVOICE_STATUSES = ['draft', 'sent', 'partial', 'paid', 'void'];

export const INVOICE_STATUS_META = {
  draft:        { label: 'Draft',         tone: 'bg-gray-500/10 text-gray-300 border-gray-500/20' },
  sent:         { label: 'Sent',          tone: 'bg-blue-500/10 text-blue-300 border-blue-500/20' },
  // Computed lifecycle states (returned as displayStatus by the serializer).
  // The underlying invoices.status column stays in the smaller draft/sent/
  // partial/paid/void set; these labels render the richer display state.
  signed:       { label: 'Signed',        tone: 'bg-lime-500/10 text-lime-300 border-lime-500/20' },
  po_received: { label: 'PO Received',    tone: 'bg-cyan-500/10 text-cyan-300 border-cyan-500/20' },
  partial:      { label: 'Partial',       tone: 'bg-amber-500/10 text-amber-300 border-amber-500/20' },
  partial_paid: { label: 'Partial paid',  tone: 'bg-amber-500/10 text-amber-300 border-amber-500/20' },
  overdue:      { label: 'Overdue',       tone: 'bg-red-500/10 text-red-300 border-red-500/20' },
  paid:         { label: 'Paid',          tone: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/20' },
  void:         { label: 'Void',          tone: 'bg-gray-500/10 text-gray-600 border-gray-500/20 line-through' },
};

export function invoiceStatusMeta(s) {
  return INVOICE_STATUS_META[s] || { label: s || '—', tone: 'bg-gray-500/10 text-gray-400 border-gray-500/20' };
}

// Client-facing labels. The internal status (draft/sent/partial/paid/void) is
// billing-ops language ("we shipped it"); a principal cares about what they
// need to *do*. Map to action-oriented labels and factor in signature +
// overdue state.
//
// Inputs needed: invoice.status, invoice.signature_required, invoice.signed_at,
// invoice.due_at. The full row is the cheapest contract.
export function clientInvoiceStatusMeta(invoice) {
  const fallback = { label: '—', tone: 'bg-gray-500/10 text-gray-400 border-gray-500/20' };
  if (!invoice) return fallback;

  const status = invoice.status;
  if (status === 'void') {
    return { label: 'Voided', tone: 'bg-gray-500/10 text-gray-500 border-gray-500/20 line-through' };
  }
  if (status === 'paid') {
    return { label: 'Paid', tone: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/20' };
  }
  if (status === 'partial' || status === 'partial_paid') {
    return { label: 'Partially paid', tone: 'bg-amber-500/10 text-amber-300 border-amber-500/20' };
  }
  if (status === 'po_received') {
    return { label: 'Pending payment', tone: 'bg-cyan-500/10 text-cyan-300 border-cyan-500/20' };
  }

  // Live signal #1: signature still required.
  const needsSignature = invoice.signature_required === 1 && !invoice.signed_at;
  if (needsSignature) {
    return { label: 'Action required', tone: 'bg-amber-500/10 text-amber-300 border-amber-500/20' };
  }

  // Live signal #2: overdue (sent or signed but past due date).
  const today = new Date().toISOString().slice(0, 10);
  const dueDate = invoice.due_at ? String(invoice.due_at).slice(0, 10) : null;
  const isOverdue = dueDate && dueDate < today;
  if (isOverdue) {
    return { label: 'Overdue', tone: 'bg-red-500/10 text-red-300 border-red-500/20' };
  }

  // Default for sent/signed: awaiting payment from the client's perspective.
  return { label: 'Awaiting payment', tone: 'bg-blue-500/10 text-blue-300 border-blue-500/20' };
}

export const INVOICE_TRANSITIONS = {
  draft:   ['sent', 'void'],
  sent:    ['partial', 'paid', 'void'],
  partial: ['paid', 'void'],
  paid:    ['partial', 'void'],
  void:    [],
};

export const PAYMENT_METHODS = ['check', 'ach', 'wire', 'card', 'po', 'other'];

export function fmtCents(cents, currency = 'USD') {
  if (cents == null) return '—';
  const n = Number(cents) / 100;
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(n);
  } catch {
    return `$${n.toFixed(2)}`;
  }
}
